/**
 * Marketing Stream (AMS) API service for SellerOS.
 *
 * Backend endpoints:
 *   GET  /sellerPartner/ams/overview?date=YYYY-MM-DD
 *   GET  /sellerPartner/ams/stores?date=YYYY-MM-DD
 *   GET  /sellerPartner/ams/subscriptions
 *   GET  /sellerPartner/ams/actions
 *   POST /sellerPartner/ams/actions/<confirm-traffic | confirm-conversion | sync-statuses>
 *   GET  /sellerPartner/ams/tasks/<task_id>
 */
import { CURRENT_API_URL } from "../../../Utils/Constants";

const API_ROOT =
  CURRENT_API_URL ||
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE_URL) ||
  "https://api.app.simpliworks.io";

const config = {
  baseUrl: `${API_ROOT}/sellerPartner`,
  getAuthHeader: () => {
    let token = typeof window !== "undefined" ? window.localStorage.getItem("token") : null;
    if (!token && typeof window !== "undefined" && window.sessionStorage) {
      try {
        const persisted = window.sessionStorage.getItem("persist:root");
        if (persisted) {
          const root = JSON.parse(persisted);
          const loggedUser = root.loggedUser ? JSON.parse(root.loggedUser) : null;
          token = loggedUser && loggedUser.token;
        }
      } catch (e) {
        // ignore
      }
    }
    if (!token && typeof window !== "undefined" && window.store) {
      try {
        const state = window.store.getState();
        token = state && state.loggedUser && state.loggedUser.token;
      } catch (e) {
        // ignore
      }
    }
    if (token) {
      return token.startsWith("Token ") ? token : `Token ${token}`;
    }
    return null;
  },
};

export function configureAmsService(overrides) {
  Object.assign(config, overrides);
}

async function request(path, { method = "GET" } = {}) {
  const headers = { Accept: "application/json" };
  const auth = config.getAuthHeader();
  if (auth) headers.Authorization = auth;
  if (method === "POST") headers["Content-Type"] = "application/json";

  const res = await fetch(`${config.baseUrl}${path}`, { method, headers, credentials: "same-origin" });
  let data = {};
  try {
    data = await res.json();
  } catch (e) {
    data = {};
  }
  // 409 = button already running; the caller shows that run's progress instead of failing
  if (!res.ok && res.status !== 409) {
    const err = new Error(data.error || data.detail || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return { status: res.status, data };
}

const query = (date, fresh) =>
  `?date=${encodeURIComponent(date)}${fresh ? "&fresh=1" : ""}`;

export const amsService = {
  getOverview: (date, fresh) => request(`/ams/overview${query(date, fresh)}`).then((r) => r.data),
  getStores: (date, fresh) => request(`/ams/stores${query(date, fresh)}`).then((r) => r.data),
  getSubscriptions: (date, fresh) => request(`/ams/subscriptions${query(date, fresh)}`).then((r) => r.data),
  getActions: () => request("/ams/actions").then((r) => r.data),
  /** -> { status: 202 | 409, data: { task_id } } */
  runAction: (action) => request(`/ams/actions/${action}`, { method: "POST" }),
  getTask: (taskId) => request(`/ams/tasks/${encodeURIComponent(taskId)}`).then((r) => r.data),
};

export default amsService;
